import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Send, LogOut, AlertCircle, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { resendConfirmation } from '@/api/auth';
import { useAuth } from '@/contexts/AuthContext';

export function ResendConfirmationPage() {
  const [isResending, setIsResending] = useState(false);
  const [cooldown, setCooldown] = useState<number | null>(null);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // Redirect if email is already confirmed
  useEffect(() => {
    if (user?.email_confirmed) {
      navigate('/projects', { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    // Check for existing cooldown in localStorage
    const storedCooldown = localStorage.getItem('emailResendCooldown');
    if (storedCooldown) {
      const cooldownEnd = parseInt(storedCooldown);
      const now = Date.now();
      const timeLeft = Math.ceil((cooldownEnd - now) / 1000);

      if (timeLeft > 0) {
        setCooldown(timeLeft);
      } else {
        localStorage.removeItem('emailResendCooldown');
      }
    }
  }, []);

  useEffect(() => {
    if (cooldown === null || cooldown <= 0) return;

    const timer = setInterval(() => {
      setCooldown(prev => {
        if (prev === null || prev <= 1) {
          localStorage.removeItem('emailResendCooldown');
          return null;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [cooldown]);

  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const handleResendEmail = async () => {
    setIsResending(true);

    try {
      const response = await resendConfirmation();

      if (response.success) {
        toast.success('Verification email sent! Please check your inbox.');
        // Start 5-minute cooldown
        const cooldownSeconds = 300;
        setCooldown(cooldownSeconds);
        localStorage.setItem('emailResendCooldown', (Date.now() + cooldownSeconds * 1000).toString());
      } else {
        toast.error(response.error || 'Failed to send verification email');
      }
    } catch (error) {
      toast.error('Failed to send verification email. Please try again.');
    } finally {
      setIsResending(false);
    }
  };

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await logout();
      // Note: logout() in AuthContext already handles navigation to /auth/login
    } catch (error) {
      toast.error('Failed to logout');
      setIsLoggingOut(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-12 px-4">
      <div className="max-w-md mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <img
            src="/images/doploy-logo.png"
            alt="Doploy"
            className="h-8 mx-auto mb-4"
          />
        </div>

        {/* Main Card */}
        <Card className="shadow-lg">
          <CardHeader className="text-center pb-4">
            <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <Mail className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-2xl">Verify Your Email Address</CardTitle>
          </CardHeader>

          <CardContent className="space-y-6">
            <p className="text-gray-600 text-center">
              We've sent a verification email to <strong className="text-gray-900">{user.email}</strong>.
              Please check your inbox and click the verification link to activate your account.
            </p>

            {/* Info Alert */}
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <strong className="block mb-2">Didn't receive the email?</strong>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  <li>Check your spam or junk folder</li>
                  <li>Make sure the email address is correct</li>
                  <li>Wait a few minutes and try resending</li>
                </ul>
              </AlertDescription>
            </Alert>

            {/* Resend Button */}
            <div className="space-y-4">
              <Button
                onClick={handleResendEmail}
                disabled={isResending || cooldown !== null}
                className="w-full"
                size="lg"
              >
                {isResending ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Sending...
                  </>
                ) : cooldown !== null ? (
                  <>
                    <Clock className="w-4 h-4 mr-2" />
                    Email Sent ({formatTime(cooldown)})
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Resend Verification Email
                  </>
                )}
              </Button>
            </div>

            <hr className="my-6" />

            {/* Logout Button */}
            <div className="text-center">
              <Button
                variant="outline"
                onClick={handleLogout}
                disabled={isLoggingOut}
                className="w-full"
              >
                {isLoggingOut ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
                    Logging out...
                  </>
                ) : (
                  <>
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center mt-6">
          <p className="text-sm text-gray-500">
            Need help? Contact us at{' '}
            <a
              href="mailto:support@doploy.io"
              className="text-blue-600 hover:text-blue-800"
            >
              support@doploy.io
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
